// @ts-nocheck comment

import {Component, inject, OnInit, Output, EventEmitter, HostListener, ViewChild, ElementRef, OnDestroy} from '@angular/core';
import {Subject, Observable, fromEvent, switchMap, takeUntil, pairwise, retry, filter, forkJoin, from, tap, share} from 'rxjs';
import {WebcamImage, WebcamInitError, WebcamUtil} from 'ngx-webcam';
import { ActivatedRoute, Router, RoutesRecognized } from '@angular/router';
import { icons } from 'src/assets/images/icons';
import { Location } from '@angular/common';
import { TasksService } from 'src/app/tasks/services/tasksService/tasks.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ImageService } from 'src/app/core/services/imageService/image.service';
import { DatabaseService } from 'src/app/core/services/databaseService/database.service';
import { NetworkStatusService } from 'src/app/core/services/networkStatusService/network-status.service';
import { PhotoDraftService } from 'src/app/core/services/photoDraftService/photo-draft.service';
import { taskContextKey } from 'src/app/core/services/photoDraftService/photo-draft.model';
import { syncErrorMessage } from 'src/app/core/services/dataService/sync-error.util';
import { TaskOfflineService } from 'src/app/tasks/offline/task-offline.service';
import { canDecodeImage, UNSUPPORTED_IMAGE_KEY } from 'src/app/shared/utils/image-decode';

@Component({
    selector: 'app-camera',
    templateUrl: './camera.component.html',
    styleUrls: ['./camera.component.scss'],
    standalone: false
})


export class CameraComponent implements OnInit, OnDestroy {
  @ViewChild('canvas', {static: true}) myCanvas!: ElementRef;
  @Output()
  public pictureTaken = new EventEmitter<WebcamImage>();

  public networkOnline: boolean | undefined;

  public exitIcon: string = icons.exitIcon;
  public imageIcon: string = icons.imageIcon;
  public switchCamera: string = icons.switchCameraIcon;
  public undoIcon: string = icons.undoIcon;
  public redoIcon: string = icons.redoIcon;
  public checkIcon: string = icons.checkIconNoCircle;

  public backButtonPath: string[];
  private projectId: string;
  private journalId: string;
  private sharedLinkId: string | null = null;
  private mode: string;
  private noteId: string;
  private journalView: string

  // toggle webcam on/off
  public showWebcam = false;
  public allowCameraSwitch = true;
  public multipleWebcamsAvailable = false;
  public facingMode: string = 'environment';
  public deviceId: string;
  public pictureUrl: string;
  public modPictureUrl: string;
  public errors: WebcamInitError[] = [];
  public deviceHeight: number = 500
  public screenWidth: number = 0
  public screenHeight: number = 0

  private steps: number;
  private currentStep: number;
  private isDrawing: boolean;
  private drawnImages: {step: number, currentPosY: number, currentPosX: number, prevPosX: number, prevPosY: number, brushSize: number, brushColor: string}[] = []
  private filteredDrawnImages: {step: number, currentPosY: number, currentPosX: number, prevPosX: number, prevPosY: number, brushSize: number, brushColor: string}[] = []

  // webcam snapshot trigger
  private trigger: Subject<void> = new Subject<void>();
  // switch to next / previous / specific webcam; true/false: forward/backwards, string: deviceId
  private nextWebcam: Subject<boolean|string> = new Subject<boolean|string>();

  private takingPicture: boolean = true
  public editingPicture: boolean = false
  private editImageData: string | null | any = null;
  private editUploadedImageData: string | null | any = null;
  public cameraUploadLoading: boolean = false;
  /** true once the source picture is drawn on the canvas; until then confirm must not export an empty canvas */
  public pictureLoaded = false;
  /** set once the editor closes (destroyed or rejected): late image errors must not navigate back again */
  private leaving = false;

  private cx: CanvasRenderingContext2D;

  private brushSize: number;
  public brushColor: string;
  public redActive: boolean = false
  public whiteActive: boolean = false
  public blackActive: boolean = true
  public yellowActive: boolean = false
  public greenActive: boolean = false
  public smallBrushActive: boolean = false
  public mediumBrushActive: boolean = true
  public largeBrushActive: boolean = false

  private uploadAlert: string = "Uploaded successfully";
  private updateAlert: string = "Updated successfully";
  private failedDraftId?: number;

  @HostListener('window:resize', ['$event'])
  onResize(event?: Event) {
    const win = !!event ? (event.target as Window) : window;
    this.screenWidth = win.innerWidth;
    this.screenHeight = win.innerHeight;
  }

  public previousUrl: string

  private readonly taskOffline = inject(TaskOfflineService);

  constructor(
    private networkStatusService: NetworkStatusService,
    private dbService: DatabaseService,
    private imageService: ImageService,
    private route: ActivatedRoute,
    public router: Router,
    private location: Location,
    private toastr: ToastrService,
    private translate: TranslateService,
    private tasksService: TasksService,
    private activatedRoute: ActivatedRoute,
    private photoDraftService: PhotoDraftService,
  ) {
    this.onResize();
    const prevNav = this.router.getCurrentNavigation().previousNavigation;
    if(prevNav?.finalUrl) {
      this.previousUrl = this.router.getCurrentNavigation().previousNavigation.finalUrl.toString();
    }
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    
    window.addEventListener('resize', () => {
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    });
  }

  public ngOnInit(): void {
    this.networkStatusService.connected$.subscribe(status => {
      this.networkOnline = status;
    })

    this.route.params.subscribe(params => {
      const { projectId, mode, journalId, sharedLinkId, noteId, view } = params;
      this.projectId = projectId;
      this.mode = mode;
      this.journalId = journalId;
      this.noteId = noteId;
      this.journalView = view;
      this.sharedLinkId = sharedLinkId;

      this.backButtonPath = ['/chat', projectId, mode, journalId];
    })

    WebcamUtil.getAvailableVideoInputs()
      .then((mediaDevices: MediaDeviceInfo[]) => {
        this.multipleWebcamsAvailable = mediaDevices && mediaDevices.length > 1;
      });

    this.translate
      .get([
        'alert.update',
        'alert.upload-success'
      ])
      .subscribe((translations: { [key: string]: string }) => {
        this.updateAlert = translations['alert.update'];
        this.uploadAlert = translations['alert.upload-success']
      });

    this.getEditImage();
  }

  ngOnDestroy(): void {
    this.leaving = true;
    this.dbService.deleteEditPicture();
    this.dbService.deleteEditUploadedImages();
    this.showWebcam = false;
  }

  async getEditImage() {
    const pendingEditPicture = from(this.dbService.getPendingEditPicture());
    const pendingEditUploadedImages = from(this.dbService.getPendingEditUploadedImages());
  
    forkJoin([pendingEditPicture, pendingEditUploadedImages]).pipe(
      tap(results => {
        results.forEach((res, index) => {
          if (!!res) {
            const imageData = index === 0 ? 'editImageData' : 'editUploadedImageData';
            this[imageData] = res.data;
            void this.openEditImage(res.data.data);
            this.showWebcam = false;
          } else {
            this.showWebcam = true;
          }
        });
      })
    ).subscribe();  
  }

  /** HEIC in Chrome (or a broken file) cannot be decoded: no editor, no upload, toast instead (silent black photo before) */
  private async openEditImage(dataUrl: string): Promise<void> {
    if (!(await canDecodeImage(dataUrl))) {
      this.rejectUnsupportedImage();
      return;
    }
    this.handleImage(this.base64ToWebcamImage(dataUrl));
  }

  private rejectUnsupportedImage(): void {
    this.pictureLoaded = false;
    if (this.leaving) return;
    this.leaving = true;
    this.cameraUploadLoading = false;
    this.toastr.error(this.translate.instant(UNSUPPORTED_IMAGE_KEY), '', { positionClass: 'toast-top-center' });
    this.location.back();
  }

  public async canvasInit(): Promise<void>{
    this.pictureLoaded = false;
    const canvasEl: HTMLCanvasElement = this.myCanvas.nativeElement as HTMLCanvasElement
    this.cx = canvasEl.getContext('2d');
    this.brushColor= '#000';
    this.brushSize = 5;
    this.steps = -1
    this.drawBackgroundPicture()
    this.captureEvents(canvasEl);
    //this.cx.lineCap = 'round';
  }

  public waitForImageToLoad(imageElement: HTMLImageElement){
    return new Promise((resolve, reject) => {
      imageElement.onload = resolve;
      imageElement.onerror = reject;
    });
  }
  
  public drawBackgroundPicture() {
    const myImage = new Image();
    myImage.src = this.pictureUrl;
    const canvasEl: HTMLCanvasElement = this.myCanvas.nativeElement;
    return this.waitForImageToLoad(myImage).then(() => {
      // a picture that cannot be drawn closes the editor like the decode check
      if (!myImage.naturalWidth || !myImage.naturalHeight) {
        this.rejectUnsupportedImage();
        return;
      }
      this.pictureLoaded = true;
      // This should be the height of the image display area, not the entire screen
      const displayAreaHeight = canvasEl.clientHeight;
      const ratio = myImage.width / myImage.height;
      const scaledWidth = displayAreaHeight * ratio;
      
      canvasEl.width = scaledWidth;
      // Ensure this matches the height where the image is displayed
      canvasEl.height = displayAreaHeight;
      
      const ctx = canvasEl.getContext('2d');
      ctx.drawImage(myImage, 0, 0, scaledWidth, displayAreaHeight);

      this.filteredDrawnImages.map(draw => {
        ctx.strokeStyle = draw.brushColor;
        ctx.lineWidth = draw.brushSize;
        this.drawOnCanvas({x: draw.prevPosX, y: draw.prevPosY}, {x: draw.currentPosX, y: draw.currentPosY});
      });
    }, () => this.rejectUnsupportedImage());
  }
  
  // public getImageDimensions(file: string) {
  //   return new Promise (function (resolved, rejected) {
  //     const i = new Image()
  //     i.onload = function(){
  //       resolved({w: i.width, h: i.height})
  //     };
  //     i.src = file
  //   })
  // }

  private captureEvents(canvasEl: HTMLCanvasElement) {
    fromEvent(canvasEl, 'touchstart')
    .pipe(
      switchMap((e) => {
        this.steps < this.currentStep?this.steps = this.currentStep:undefined
        this.steps++
        this.drawnImages = this.filteredDrawnImages
        this.isDrawing = true
        this.cx.lineWidth = this.brushSize
        this.cx.strokeStyle = this.brushColor
        // after a mouse down, we'll record all mouse moves
        e.preventDefault()
        return fromEvent(canvasEl, 'touchmove')
          .pipe(
            takeUntil(fromEvent(canvasEl, 'touchend')),
            pairwise()
          )
      })
    )
    .subscribe((res: [TouchEvent, TouchEvent]) => {
      const rect = canvasEl.getBoundingClientRect();
      // previous and current position with the offset
      const prevPos = {
        x: res[0].changedTouches[0].clientX - rect.left,
        y: res[0].changedTouches[0].clientY - rect.top
      };
      const currentPos = {
        x: res[1].changedTouches[0].clientX - rect.left,
        y: res[1].changedTouches[0].clientY - rect.top
      };
      this.pushDrawing(currentPos.x, currentPos.y, prevPos.x, prevPos.y)
      this.drawOnCanvas(prevPos, currentPos);
    });

    // this will capture all mousedown events from the canvas element
    fromEvent(canvasEl, 'mousedown')
    .pipe(
      switchMap((e) => {
        this.steps < this.currentStep?this.steps = this.currentStep:undefined
        this.steps++
        this.drawnImages = this.filteredDrawnImages
        this.isDrawing = true
        this.cx.lineWidth = this.brushSize
        this.cx.strokeStyle = this.brushColor
        // after a mouse down, we'll record all mouse moves
        return fromEvent(canvasEl, 'mousemove')
          .pipe(
            // we'll stop (and unsubscribe) once the user releases the mouse
            // this will trigger a 'mouseup' event
            takeUntil(fromEvent(canvasEl, 'mouseup')),
            // we'll also stop (and unsubscribe) once the mouse leaves the canvas (mouseleave event)
            takeUntil(fromEvent(canvasEl, 'mouseleave')),
            // pairwise lets us get the previous value to draw a line from
            // the previous point to the current point
            pairwise()
          )
      })
    )
    .subscribe((res: [MouseEvent, MouseEvent]) => {
      const rect = canvasEl.getBoundingClientRect();
      // previous and current position with the offset
      const prevPos = {
        x: res[0].clientX - rect.left,
        y: res[0].clientY - rect.top
      };
      const currentPos = {
        x: res[1].clientX - rect.left,
        y: res[1].clientY - rect.top
      };
      this.pushDrawing(currentPos.x, currentPos.y, prevPos.x, prevPos.y)
      this.drawOnCanvas(prevPos, currentPos);
    });

    fromEvent(canvasEl, 'touchend').subscribe(()=>{
      this.currentStep = this.steps
    })
    fromEvent(canvasEl, 'mouseup').subscribe(()=>{
      this.currentStep = this.steps
    })
    fromEvent(canvasEl, 'mouseleave').subscribe(()=>{
      this.currentStep = this.steps
    })
  }

  public pushDrawing(currentPosX: number, currentPosY: number, prevPosX: number, prevPosY: number){
    this.drawnImages.push({
      step: this.steps,
      currentPosY: currentPosY,
      currentPosX: currentPosX,
      prevPosX: prevPosX,
      prevPosY: prevPosY,
      brushSize: this.brushSize,
      brushColor: this.brushColor
    })
  }

  private drawOnCanvas(prevPos: { x: number, y: number }, currentPos: { x: number, y: number }) {
    if (!this.cx) { return; }
    this.cx.beginPath();

    const rect = this.myCanvas.nativeElement.getBoundingClientRect();

    // Get the displayed image dimensions
    const image = new Image();
    image.src = this.pictureUrl;
    const imageAspectRatio = image.naturalWidth / image.naturalHeight;
    const canvasAspectRatio = rect.width / rect.height;

    let renderHeight, renderWidth;
    if (canvasAspectRatio > imageAspectRatio) {
        // Canvas is wider than image's aspect ratio
        renderHeight = rect.height;
        renderWidth = renderHeight * imageAspectRatio;
    } else {
        // Canvas is taller or equal to the image's aspect ratio
        renderWidth = rect.width;
        renderHeight = renderWidth / imageAspectRatio;
    }

    const scaleX = this.myCanvas.nativeElement.width / renderWidth;
    const scaleY = this.myCanvas.nativeElement.height / renderHeight;

    // Calculate offsets if the image is centered within the canvas
    const offsetX = (rect.width - renderWidth) / 2;
    const offsetY = (rect.height - renderHeight) / 2;

    // Apply scale and offset to previous and current positions
    const scaledPrevPos = {
        x: (prevPos.x - offsetX) * scaleX,
        y: (prevPos.y - offsetY) * scaleY
    };
    const scaledCurrentPos = {
        x: (currentPos.x - offsetX) * scaleX,
        y: (currentPos.y - offsetY) * scaleY
    };

    this.cx.moveTo(scaledPrevPos.x, scaledPrevPos.y);
    this.cx.lineTo(scaledCurrentPos.x, scaledCurrentPos.y);
    this.cx.stroke();
  }


  public async undoDrawing(){
    if (this.currentStep > -1){
      this.isDrawing = false
      this.currentStep--
      this.filteredDrawnImages = this.drawnImages.filter(x => x.step <= this.currentStep)
      //this.cx.clearRect(0,0,this.myCanvas.nativeElement.width, this.myCanvas.nativeElement.height)
      //await this.canvasInit()
      this.drawBackgroundPicture()
    }
  }

  public async redoDrawing(){
    if(!this.isDrawing && this.currentStep < this.steps){
      this.currentStep++
      this.filteredDrawnImages = this.drawnImages.filter(x => x.step <= this.currentStep)
      //this.cx.clearRect(0,0,this.myCanvas.nativeElement.width, this.myCanvas.nativeElement.height)
      //await this.canvasInit()
      this.drawBackgroundPicture()
    }
  }

  public async saveCanva(): Promise<void> {
    // export guard: never upload the empty canvas of a picture that did not load
    if (!this.pictureLoaded) return;
    this.cameraUploadLoading = true;
    if (this.journalView === 'new-task') {
      this.storeImageToTask();
      return;
    }
    if (this.editImageData) {
      // editing a picture that is on the server replaces it there: online-only (spec §2)
      if (!this.networkOnline) {
        this.cameraUploadLoading = false;
        this.toastr.error(this.translate.instant('offline.online-only'), '', { positionClass: 'toast-top-center' });
        return;
      }
      this.editImgData();
      return;
    }
    if (this.sharedLinkId) {
      // shared-link guests stay online-only (plan D13): the former offline path sent them to the wrong route
      if (!this.networkOnline) {
        this.cameraUploadLoading = false;
        this.toastr.error(this.translate.instant('offline.guest-offline'), '', { positionClass: 'toast-top-center' });
        return;
      }
      void this.addImage();
      return;
    }
    await this.addMemberImage();
  }

  /** members: the draft stays as crash safety until the picture is in the outbox or on the server (Q1) */
  private async addMemberImage(): Promise<void> {
    const file = this.canvasToJPEGFile();
    const draftId = await this.photoDraftService
      .add({ contextKey: taskContextKey(this.noteId, null), returnUrl: (this.previousUrl ?? '').replace(/\/photo-gallery$/, ''), label: '' }, file)
      .catch(() => undefined);
    // while the upload runs (up to 120 s) the details page must not offer this draft for «Wiederherstellen» (M1)
    if (draftId !== undefined) this.imageService.markUploading([draftId]);
    try {
      const result = await this.taskOffline.addPhotos({ projectId: this.projectId, taskId: this.noteId }, [file]);
      const failure = result.failedPhotos[0]?.message ?? (result.rejectedPhotos.length ? this.translate.instant('photo-drafts.quota') : null);
      if (failure) {
        this.handleAddImageError(draftId, failure);
        return;
      }
      if (draftId !== undefined) void this.photoDraftService.remove(draftId);
      if (this.failedDraftId !== undefined && this.failedDraftId !== draftId) void this.photoDraftService.remove(this.failedDraftId).catch(() => undefined);
      this.failedDraftId = undefined;
      this.cameraUploadLoading = false;
      const message = result.queued ? this.translate.instant('offline.saved-on-device') : this.uploadAlert;
      this.toastr.success(message, '', { positionClass: 'toast-top-center' });
      if (this.journalView === 'chat') this.goBackToPrevPage();
      else this.onSaveNavigateTo();
    } catch (error: unknown) {
      this.handleAddImageError(draftId, syncErrorMessage(error));
    } finally {
      if (draftId !== undefined) this.imageService.unmarkUploading(draftId);
    }
  }

  private editImgData() {
    if(!this.sharedLinkId) {
      this.tasksService.deleteImage(this.noteId, this.editImageData.key).subscribe((res) => {
        this.tasksService.addImage(this.noteId, this.canvasToJPEGFile()).subscribe(data => {
          if(data.success){
            this.cameraUploadLoading = false;
            this.toastr.success(this.updateAlert, '', {
              positionClass: 'toast-top-center',
            });
            if (this.journalView === 'chat') {
              this.goBackToPrevPage();
            } else {
              this.onSaveNavigateTo(); 
            }
          }
        });
      });
    } else {
      this.tasksService.deleteSharedTaskImage(this.sharedLinkId, this.noteId, this.editImageData.key).subscribe((res) => {
        this.tasksService.addImageToSharedTasks(this.sharedLinkId, this.noteId, this.canvasToJPEGFile()).subscribe(data => {
          if(data.success){
            this.cameraUploadLoading = false;
            this.toastr.success(this.uploadAlert, '', {
              positionClass: 'toast-top-center',
            });
            if (this.journalView === 'chat') {
              this.goBackToPrevPage();
            } else {
              this.onSaveNavigateTo(); 
            }
          }
        });
      })
    }
  }

  private async addImage() {
    const file = this.canvasToJPEGFile();
    const draftId = await this.photoDraftService.add({
      contextKey: taskContextKey(this.noteId, this.sharedLinkId),
      returnUrl: (this.previousUrl ?? '').replace(/\/photo-gallery$/, ''),
      label: '',
    }, file).catch(() => undefined);
    if (draftId !== undefined) this.imageService.markUploading([draftId]);

    if (draftId !== undefined && this.failedDraftId !== undefined && this.failedDraftId !== draftId) {
      void this.photoDraftService.remove(this.failedDraftId).catch(() => undefined);
      this.failedDraftId = undefined;
    }

    const upload$ = this.tasksService.addImageToSharedTasks(this.sharedLinkId, this.noteId, file);

    upload$.subscribe({
      next: (data: { success?: boolean; error?: string } | null) => {
        if (data?.success) {
          if (draftId !== undefined) {
            void this.photoDraftService.remove(draftId);
            this.imageService.unmarkUploading(draftId);
          }
          if (this.failedDraftId !== undefined && this.failedDraftId !== draftId) {
            void this.photoDraftService.remove(this.failedDraftId).catch(() => undefined);
          }
          this.failedDraftId = undefined;
          this.cameraUploadLoading = false;
          this.toastr.success(this.uploadAlert, '', {
            positionClass: 'toast-top-center',
          });
          if (this.journalView === 'chat') {
            this.goBackToPrevPage();
          } else {
            this.onSaveNavigateTo();
          }
        } else {
          this.handleAddImageError(draftId, data?.error ?? 'success: false');
        }
      },
      error: (error) => this.handleAddImageError(draftId, syncErrorMessage(error)),
    });
  }

  private handleAddImageError(draftId: number | undefined, message: string) {
    this.failedDraftId = draftId ?? this.failedDraftId;
    if (draftId !== undefined) {
      this.imageService.unmarkUploading(draftId);
      void this.photoDraftService.markFailed(draftId, message);
    }
    this.cameraUploadLoading = false;
    this.toastr.error(message, '', { positionClass: 'toast-top-center' });
  }

  private storeImageToTask() {
    this.imageService.storeImage(this.canvasToJPEGFile())
    .then(() => {
      this.cameraUploadLoading = false;
      this.goBackToPrevPage();
    });
  }

  private canvasToJPEGFile() {
    const base64Data = this.myCanvas.nativeElement.toDataURL('image/jpeg');
    const blob = this.base64ToBlob(base64Data);
    const imageFile = new File([blob], 'canvas_image.jpeg', { type: 'image/jpeg' });
    return imageFile;
  }

  private base64ToBlob(base64: string, type: string = 'image/jpeg'): Blob {
    const binaryString = window.atob(base64.split(',')[1]);
    const len = binaryString.length;
    const binaryArray = new Uint8Array(len);
    
    for (let i = 0; i < len; i++) {
      binaryArray[i] = binaryString.charCodeAt(i);
    }
  
    return new Blob([binaryArray], {type: type});
  }

  // ************************************************

  public triggerSnapshot(): void {
    this.trigger.next();
  }

  public toggleWebcam(): void {
    this.showWebcam = !this.showWebcam;
  }

  public handleInitError(error: WebcamInitError): void {
    this.errors.push(error);
  }

  public showNextWebcam(directionOrDeviceId: boolean|string): void {
    // true => move forward through devices
    // false => move backwards through devices
    // string => move to device with given deviceId
    this.nextWebcam.next(directionOrDeviceId);
  }

  public handleImage(webcamImage: WebcamImage): void {
    this.pictureUrl = webcamImage.imageAsDataUrl
    this.takingPicture = false
    this.editingPicture = true
    this.canvasInit()
    this.pictureTaken.emit(webcamImage);
  }

  base64ToWebcamImage(base64Data: string): WebcamImage {    
    // Assuming the base64Data string is in the format 'data:[<MIME-type>];base64,[<data>]'
    const mimeType = base64Data.match(/data:([^;]+);base64/)?.[1];
    
    return {
      _mimeType: mimeType || "image/jpeg",  // Defaulting to "image/jpeg" if MIME type is not found
      _imageAsBase64: base64Data.split('base64,')[1],  // Extract the Base64 encoded data
      _imageAsDataUrl: base64Data,  // Full data URL,
      imageAsDataUrl: base64Data
    };
  };
  
  public cameraWasSwitched(deviceId: string): void {
    this.deviceId = deviceId;
  }

  public get triggerObservable(): Observable<void> {
    return this.trigger.asObservable();
  }

  public get nextWebcamObservable(): Observable<boolean|string> {
    return this.nextWebcam.asObservable();
  }

  public async save(blob: Blob): Promise<void>{
    this.modPictureUrl = await this.blobToBase64(blob)
    this.editingPicture = false
  }

  public async blobToBase64(blob: Blob): Promise<string>{
    return blob
  }

  public isTakingPicture(){
    return this.takingPicture
  }

  public isEditingPicture(){
    return this.editingPicture
  }

  public get videoOptions(): MediaTrackConstraints {
    const result: MediaTrackConstraints = {};
    if (this.facingMode && this.facingMode !== '') {
      result.facingMode = { ideal: this.facingMode };
    }
    return result;
  }

  public changeBrushSize(lineWidth: number): void{
    //console.log('change brush size')
    this.brushSize = lineWidth;
    this.smallBrushActive = false
    this.mediumBrushActive = false
    this.largeBrushActive = false
    switch(lineWidth){
      case 1:
        this.smallBrushActive = true
        break
      case 5:
        this.mediumBrushActive = true
        break
      case 10:
        this.largeBrushActive = true
        break
      default:
        return
    }
  }

  public changeBrushColor(color: string): void{
    //console.log('change brush color')
    this.brushColor = color;
    this.redActive = false
    this.greenActive = false
    this.whiteActive = false
    this.blackActive = false
    this.yellowActive = false

    switch(color){
      case '#ffffff':
        this.whiteActive = true
        break
      case '#000000':
        this.blackActive = true
        break
      case '#ff0000':
        this.redActive = true
        break
      case '#008000':
        this.greenActive = true
        break
      case '#ffff00':
        this.yellowActive = true
        break
      default:
        return
    }
  }

  goBackToPrevPage() {
    this.location.back();
  }
  
  onSaveNavigateTo() {
    if(this.previousUrl.includes('photo-gallery')){
      this.router.navigate([this.previousUrl]);
    } else {
      this.router.navigate([this.previousUrl, 'photo-gallery'])
    }
  }

}
